import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatCurrency } from 'utils/user'

import { useTheme, styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'

import { getCurrentDate } from 'constants/index'
import actionTypes from 'store/actions'

import ConnectButton from 'components/Common/Button/ConnectButton'
import SectionTitle from 'components/Common/SectionTitle'
import ToolTip from 'components/Common/ToolTip'
import Overlay from 'components/Common/Overlay'
import ConfirmDialog from 'components/Common/Dialog/ConfirmDialog'
import ButtonTooltip from 'components/Common/ToolTip/ButtonTooltip'

import linkOffSvg from 'assets/link-off.svg'
import api from 'api'

const items = [
  {
    title: 'shopify',
    logoUrl: process.env['REACT_APP_BRC_IMAGE_URL'] + '/06302022/Shopify_Logo.svg',
  },
  {
    title: 'stripe',
    logoUrl: process.env['REACT_APP_BRC_IMAGE_URL'] + '/06302022/Stripe_Logo.svg',
  },
]

const CardHeader = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const CardBody = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const SalesCard = props => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const userInfo = useSelector(state => state.userReducer)
  const isFreeAccount = userInfo?.user?.accountType !== 'premium'
  const commerceCodatConnected = useSelector(state => state.appReducer.commerceCodatConnected)
  const codatIntegrated = useSelector(state => state.apiResponseReducers.codatIntegrated)
  const accountCodatConnected = useSelector(state => state.appReducer.accountCodatConnected)
  const companyId = useSelector(state => state.apiResponseReducers.companyId)
  const connectionId = useSelector(state => state.appReducer.commerceConnectionId)

  const salesData = useSelector(state => state.salesReducer.sales)

  const [confirmationDialog, setConfirmationDialog] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)

  useEffect(() => {
    if (isFreeAccount || !commerceCodatConnected) setShowOverlay(false)
  }, [isFreeAccount, commerceCodatConnected])

  const onClickDisconnect = async () => {
    try {
      await api.disconnectApp(companyId, connectionId)
      dispatch({ type: actionTypes.COMMERCECODATDISCONNECTED })
    } catch (error) {
      console.log(error)
    }
  }

  const handleClickConnect = e => {
    e && e.preventDefault()
    handleCodat()
  }

  const handleMouseEnter = () => {
    setShowOverlay(true)
  }

  const handleMouseLeave = () => {
    setShowOverlay(false)
  }

  const handleCodat = () => {
    if (isFreeAccount) {
      dispatch({ type: actionTypes.TOGGLESUBSCRIPTIONMODAL, data: true })
    } else {
      if (codatIntegrated) {
        if (accountCodatConnected && !commerceCodatConnected) {
          window.location.replace(`https://links.codat.io/company/${companyId}/link/commerce`)
        } else if (commerceCodatConnected && !accountCodatConnected) {
          window.location.replace(`https://links.codat.io/company/${companyId}/link/accounting`)
        } else {
          window.location.replace(`https://links.codat.io/company/${companyId}`)
        }
      } else {
        dispatch({ type: actionTypes.TOGGLECOMPANYNAMEMODAL, data: true })
      }
    }
  }

  // const handleRefresh = () => {
  //   if (commerceCodatConnected) {
  //     setOnRefresh(true)
  //     api.getSalesData(companyId, daySpanSelected)
  //   }
  // }

  const tooltipContent = (
    <Typography variant="body2" color="white">
      Link your eCommerce software for quick easy access.
    </Typography>
  )

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {showOverlay && (!commerceCodatConnected || (commerceCodatConnected && isFreeAccount)) && (
        <Overlay>
          <Box
            sx={{
              height: 'calc(100% - 100px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-around',
            }}
          >
            <Typography
              variant="h5"
              color="white"
              sx={{
                background: theme.palette.background.primary,
                WebkitTextFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
              }}
            >
              Manage Sales
            </Typography>
            <Typography
              component="div"
              variant="body1B"
              color="white"
              sx={{
                textAlign: 'center',
                width: '80%',
                marginBottom: 2,
              }}
            >
              Connect your point-of-sale or eCommerce software to see your customer transactions, sales volume, and any
              disputes.
            </Typography>
            <Box>
              <ImageList cols={2} gap={15}>
                {items.map(item => (
                  <ImageListItem
                    key={item.title}
                    sx={{
                      width: '80px',
                      height: '80px !important',
                      padding: '10px',
                      background: 'white',
                      borderRadius: 2,
                    }}
                  >
                    <img src={item.logoUrl} alt={item.title} loading="lazy" style={{ objectFit: 'contain' }}></img>
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          </Box>
          {isFreeAccount && (
            <Box marginTop="30px">
              <ConnectButton label="Connect your sales app" />
            </Box>
          )}
        </Overlay>
      )}
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '25px',
        }}
      >
        <CardHeader>
          <Box>
            <SectionTitle title="Sales" />
            &nbsp;
            <ToolTip content={tooltipContent} />
          </Box>
          <Box sx={{ display: 'flex' }}>
            {commerceCodatConnected && (
              <ButtonTooltip
                placement={'top'}
                title={'Disconnect App'}
                arrow
                onClick={() => setConfirmationDialog(true)}
              >
                <img
                  src={linkOffSvg}
                  height="20px"
                  width="20px"
                  alt={'sales-disconnect-button'}
                  style={{ cursor: 'pointer' }}
                />
              </ButtonTooltip>
            )}
          </Box>
        </CardHeader>
        <Typography
          component="div"
          variant="overline1"
          sx={{
            float: 'left',
            color: theme.palette.color.darkBlue50,
            marginTop: '8px',
          }}
        >
          DAILY - {getCurrentDate()}
        </Typography>

        {!commerceCodatConnected && (
          <CardBody
          // onClick={() => {
          //   dispatch({ type: actionTypes.SHOWSALESDIALOG })
          // }}
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                position: 'relative',
                alignItems: 'center',
              }}
            >
              <Box marginTop="30px">
                <Typography variant="body1M" color="primary">
                  Transactions:
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.blueLight,
                  }}
                >
                  {'1,537'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body1M" color="primary">
                  Sales Volume:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.aqua110,
                  }}
                >
                  {'$742'}
                </Typography>
              </Box>

              <Box marginBottom="15px">
                <Typography variant="body1M" color="primary">
                  Disputes:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.negative,
                  }}
                >
                  {5}
                </Typography>
              </Box>
              <Typography
                variant="h2"
                sx={{
                  cursor: 'default',
                  position: 'absolute',
                  opacity: '0.4',
                  userSelect: 'none',
                  color: theme.palette.color.darkBlue10,
                  mixBlendMode: 'multiply',
                }}
              >
                Sample Data
              </Typography>
            </Box>

            {isFreeAccount && <ConnectButton label="Connect your sales app" />}
            {!isFreeAccount && (
              <ConnectButton
                sx={{ zIndex: theme.zIndex.overlay }}
                onClick={handleClickConnect}
                label="Connect your sales app"
                isConnected={commerceCodatConnected}
              />
            )}
          </CardBody>
        )}
        {commerceCodatConnected && (
          <CardBody
            onClick={() => {
              dispatch({ type: actionTypes.SHOWSALESDIALOG })
            }}
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                alignItems: 'center',
              }}
            >
              <Box marginTop="30px">
                <Typography variant="body1M" color="primary">
                  Transactions:
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.blueLight,
                  }}
                >
                  {salesData.transaction_count}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body1M" color="primary">
                  Sales Volume:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.aqua110,
                  }}
                >
                  {formatCurrency(parseFloat(salesData.sales_volume || 0)) || '$0'}
                </Typography>
              </Box>

              <Box marginBottom="10px">
                <Typography variant="body1M" color="primary">
                  Disputes:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.color.negative,
                  }}
                >
                  {salesData.dispute_count}
                </Typography>
              </Box>
            </Box>
          </CardBody>
        )}
      </Box>
      <ConfirmDialog
        show={confirmationDialog}
        setShow={setConfirmationDialog}
        onSuccess={onClickDisconnect}
        SuccessMessage="Disconnect"
        title="Are you sure?"
        content="If so, you will have the opportunity to log back in and reconnect your application to display company data in this tile."
        content2="Do you want to proceed with disconnecting your application from this tile?"
      />
    </Box>
  )
}

export default SalesCard
